/**
 * Las rutas del juego. Tres, y a mano.
 *
 * Sin `react-router` y no por purismo: son tres, el enrutador pesa más que ellas, y el
 * estado de esta app no es una jerarquía de vistas sino una sesión de Colyseus con
 * paneles encima. Un enrutador de verdad querría mandar sobre ese estado, y acá la
 * relación es al revés — la URL *acompaña* a la sesión.
 *
 * De ahí la forma de este archivo, que es lo único que hay que entender para tocarlo:
 *
 * · **Hacia adentro es un EVENTO.** Escribir una URL, pulsar atrás o abrir el juego
 *   producen una ruta que la app *consume* una vez.
 * · **Hacia afuera es una ESCRITURA.** Cuando la app cambia de sitio, corrige la barra
 *   de direcciones y ya está; nadie la escucha de vuelta.
 *
 * Partirlo así es lo que evita el bucle clásico: si la URL fuera estado de React en las
 * dos direcciones, cada corrección dispararía una aplicación, que dispararía otra
 * corrección. Acá no hay ciclo porque los dos sentidos no se hablan.
 *
 * Lo que NO hace una ruta, y conviene tenerlo escrito: **no da permisos**. `/taller` no
 * abre el taller; lo abre la marca `dev` comprobada en `onAuth`. Una ruta sólo dice a
 * dónde ir, y el servidor sigue contestando que no cuando toca.
 */

export type Route =
  | { kind: "juego" }
  | { kind: "taller" }
  | { kind: "club"; clubId: string };

/**
 * La ruta que dice la barra de direcciones ahora mismo.
 *
 * Lo que no reconoce cae al juego en vez de dar un 404, y es deliberado: esto es un
 * juego de una sola página servido con `try_files ... /index.html`, así que cualquier
 * URL inventada ya llega acá. Enseñar un "no existe" propio sería inventar una pantalla
 * para un caso que sólo se alcanza escribiendo mal a mano.
 */
export function readRoute(): Route {
  if (typeof window === "undefined") return { kind: "juego" };

  const partes = window.location.pathname.split("/").filter((parte) => parte.length > 0);

  if (partes[0] === "taller") return { kind: "taller" };
  if (partes[0] === "club" && partes[1]) {
    return { kind: "club", clubId: decodeURIComponent(partes[1]) };
  }

  return { kind: "juego" };
}

export function routePath(route: Route): string {
  if (route.kind === "taller") return "/taller";
  if (route.kind === "club") return `/club/${encodeURIComponent(route.clubId)}`;
  return "/";
}

/**
 * Deja la barra de direcciones diciendo dónde estás.
 *
 * `replaceState` y no `pushState`: esto corrige la URL DETRÁS de un movimiento que ya
 * ocurrió —entraste al taller, abriste una ficha— y apilar una entrada por cada uno
 * llenaría el historial de pasos que el jugador no dio. El botón de atrás sigue
 * sirviendo para lo que el jugador sí hizo: volver al enlace con el que llegó.
 *
 * No toca la query, y eso importa: el enlace de restablecer contraseña llega por ahí y
 * `takeEmailLink` lo consume al arrancar. Pisarla sería tirarle el token.
 */
export function syncRoute(route: Route): void {
  if (typeof window === "undefined") return;

  const destino = routePath(route);
  if (window.location.pathname === destino) return;

  window.history.replaceState(null, "", destino + window.location.search);
}

/** Apila una entrada de verdad. Para cuando el jugador pide ir a un sitio. */
export function pushRoute(route: Route): void {
  if (typeof window === "undefined") return;

  const destino = routePath(route);
  if (window.location.pathname === destino) return;

  window.history.pushState(null, "", destino + window.location.search);
}

/**
 * Avisa cuando la ruta cambia desde FUERA: el botón de atrás, el de adelante, un enlace.
 *
 * No se dispara con `syncRoute` ni con `pushRoute` —`popstate` sólo escucha la
 * navegación del usuario— y ahí está la mitad de por qué esto no entra en bucle.
 */
export function onRouteChange(listener: (route: Route) => void): () => void {
  if (typeof window === "undefined") return () => undefined;

  const handler = (): void => listener(readRoute());
  window.addEventListener("popstate", handler);
  return () => window.removeEventListener("popstate", handler);
}
